import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot, runTransaction } from 'firebase/firestore';
import { db } from '../config/firebase';
import firebaseAuthService from '../services/firebaseAuthService';
import universalDataService from '../services/universalDataService';
import deletedItemsService from '../services/deletedItemsService';
import { ConfirmPanel, Field, Icon, ICON, Panel, formatDate, formatHa, nameOf } from './ui';
import {
  CROP_TYPES, PRODUCT_CATEGORIES, UNITS, addInvoiceToInventory, analyzeProducts, buildInvoice, companyCropBreakdown,
  companyStats, complianceReport, emptyForm, emptyRow, formErrors, formFromInvoice, formTotal, invoiceAllocations,
  lineStatus, matchesInvoice, rowTotal, setupIssues, stockLines, targetCropsOf, transferSuggestions,
} from './stockLogic';
import './StockAdjustment.css';

const PAGE_SIZE = 20;
const VIEWS = [
  { id: 'stocks', label: 'Stocuri' }, { id: 'invoices', label: 'Facturi' }, { id: 'summary', label: 'Sumar' },
  { id: 'reports', label: 'Rapoarte' }, { id: 'setup', label: 'Configurare' },
];
const STATUS_TEXT = { ok: 'Suficient', near: 'Aproape', low: 'Lipsă', unknown: '' };

const number = (value, digits = 0) => (Number(value) || 0).toLocaleString('ro-RO', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const quantity = (value) => (Number(value) || 0).toLocaleString('ro-RO', { maximumFractionDigits: 2 });
const lei = (value) => `${number(value)} lei`;
const decimalText = (value) => value.replace(',', '.').replace(/[^0-9.]/g, '');

const readSaved = (key, fallback) => {
  try {
    const saved = JSON.parse(localStorage.getItem(key))?.data;
    if (Array.isArray(fallback)) return Array.isArray(saved) ? saved : fallback;
    return saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : fallback;
  } catch { return fallback; }
};

const readSuppliers = () => {
  try { return JSON.parse(localStorage.getItem('savedSuppliers')) || []; } catch { return []; }
};

// ---------- Stocks ----------

const StockRow = ({ line, onOpen }) => {
  const status = lineStatus(line);
  const percent = line.need > 0 ? Math.min(100, (line.quantity / line.need) * 100) : 100;
  return (
    <button type="button" className="st-stock" onClick={onOpen}>
      <span className="st-stock-top"><span>{line.type}</span><span>{quantity(line.quantity)} {line.unit}</span></span>
      {status !== 'unknown' && <span className="st-bar"><i className={`is-${status}`} style={{ width: `${percent}%` }} /></span>}
      <span className="st-stock-foot">
        <span>
          {line.general && 'Pentru toate culturile'}
          {!line.general && status !== 'unknown' && `Necesar ${quantity(line.need)} ${line.unit}`}
          {!line.general && status === 'unknown' && 'Necesar necunoscut'}
        </span>
        {STATUS_TEXT[status] && <span className={`st-status is-${status}`}>{STATUS_TEXT[status]}</span>}
      </span>
    </button>
  );
};

const ProductPanel = ({ line, onClose }) => {
  const status = lineStatus(line);
  const missing = line.need !== null ? line.need - line.quantity : 0;
  return (
    <Panel title={line.type} subtitle={line.company} onClose={onClose}>
      <div className="pm-card">
        <dl className="pm-rows">
          <div><dt>În stoc</dt><dd>{quantity(line.quantity)} {line.unit}</dd></div>
          <div><dt>Valoare</dt><dd>{lei(line.value)}</dd></div>
          {line.quantity > 0 && <div><dt>Preț mediu</dt><dd>{number(line.value / line.quantity, 2)} lei/{line.unit}</dd></div>}
          {status !== 'unknown' && <div><dt>Necesar</dt><dd>{quantity(line.need)} {line.unit}</dd></div>}
          {status !== 'unknown' && (
            <div>
              <dt>{missing > 0 ? 'Lipsesc' : 'Surplus'}</dt>
              <dd className={missing > 0 ? 'pm-warn' : ''}>{quantity(Math.abs(missing))} {line.unit}</dd>
            </div>
          )}
          {line.consumption > 0 && <div><dt>Consum mediu</dt><dd>{quantity(line.consumption)} {line.unit}/ha</dd></div>}
          {line.names.length > 0 && <div><dt>Produse</dt><dd>{line.names.join(', ')}</dd></div>}
          {line.crops.length > 0 && <div><dt>Pentru</dt><dd>{line.crops.join(', ')}</dd></div>}
        </dl>
      </div>
      {line.general && <p className="pm-note">Se folosește la toate culturile, deci nu are un necesar pe hectar.</p>}
      {status === 'unknown' && !line.general && (
        <p className="pm-note">Necesarul se calculează din hectarele culturilor și consumul pe hectar. Verifică în Configurare dacă firma are aceste culturi.</p>
      )}
      {line.cropDetails.length > 0 && (
        <div className="pm-card">
          <h3>Pe culturi</h3>
          <dl className="pm-rows">
            {line.cropDetails.map((crop) => (
              <div key={crop.cropName}>
                <dt>{crop.cropName}, {formatHa(crop.hectares)} ha{crop.isEstimated ? ' (estimat)' : ''}</dt>
                <dd>{quantity(crop.need)} {line.unit} · {quantity(crop.consumptionPerHa)}/ha</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </Panel>
  );
};

// ---------- Invoices ----------

const InvoiceRowFields = ({ row, index, errors, canRemove, onChange, onRemove }) => {
  const fuel = row.productType === 'Motorină';
  const set = (patch) => onChange(index, patch);
  const crops = [...new Set([...CROP_TYPES, ...row.targetCrops, ...(row.targetCrop ? [row.targetCrop] : [])])];

  const chooseType = (productType) => {
    const category = PRODUCT_CATEGORIES.find((item) => item.name === productType);
    set({ productType, unit: category?.unit || row.unit });
  };
  const toggleCrop = (crop) => set({
    targetCrops: row.targetCrops.includes(crop) ? row.targetCrops.filter((item) => item !== crop) : [...row.targetCrops, crop],
  });

  return (
    <section className={`pm-card ${errors[`type${index}`] || errors[`quantity${index}`] || errors[`price${index}`] || errors[`crop${index}`] ? 'has-error' : ''}`}>
      <div className="pm-card-head">
        <h3>Produsul {index + 1}</h3>
        {canRemove && <button type="button" className="pm-link-danger" onClick={() => onRemove(index)}>Scoate</button>}
      </div>
      <Field label="Tip produs" error={errors[`type${index}`]}>
        <select value={row.productType} onChange={(e) => chooseType(e.target.value)}>
          <option value="">Alege</option>
          {PRODUCT_CATEGORIES.map((category) => <option key={category.id} value={category.name}>{category.name}</option>)}
          {row.productType && !PRODUCT_CATEGORIES.some((item) => item.name === row.productType) && <option value={row.productType}>{row.productType}</option>}
        </select>
      </Field>
      {!fuel && (
        <Field label="Nume produs" hint="Marca de pe factură, de exemplu Roundup. Poate rămâne gol.">
          <input value={row.productName} onChange={(e) => set({ productName: e.target.value })} />
        </Field>
      )}
      {!fuel && (
        <>
          <label className="st-switch">
            <input type="checkbox" checked={row.isMultipleCrops}
              onChange={(e) => set(e.target.checked
                ? { isMultipleCrops: true, targetCrops: row.targetCrop ? [row.targetCrop] : [], targetCrop: '' }
                : { isMultipleCrops: false, targetCrops: [], targetCrop: '' })} />
            <span>Pentru mai multe culturi</span>
          </label>
          {row.isMultipleCrops ? (
            <div className={`st-crops ${errors[`crop${index}`] ? 'has-error' : ''}`} role="group" aria-label="Culturi">
              {crops.map((crop) => (
                <button key={crop} type="button" className={row.targetCrops.includes(crop) ? 'is-on' : ''} aria-pressed={row.targetCrops.includes(crop)} onClick={() => toggleCrop(crop)}>{crop}</button>
              ))}
            </div>
          ) : (
            <Field label="Cultura" error={errors[`crop${index}`]}>
              <select value={row.targetCrop} onChange={(e) => set({ targetCrop: e.target.value })}>
                <option value="">Alege</option>
                {crops.map((crop) => <option key={crop} value={crop}>{crop}</option>)}
              </select>
            </Field>
          )}
        </>
      )}
      <div className="pm-two">
        <Field label="Cantitate" error={errors[`quantity${index}`]}>
          <input inputMode="decimal" value={row.quantity} onChange={(e) => set({ quantity: decimalText(e.target.value) })} />
        </Field>
        <Field label="Unitate">
          <select value={row.unit} onChange={(e) => set({ unit: e.target.value })}>
            {[...new Set([...UNITS, row.unit])].map((unit) => <option key={unit} value={unit}>{unit}</option>)}
          </select>
        </Field>
      </div>
      <div className="pm-two">
        <Field label="Preț unitar (lei)" error={errors[`price${index}`]}>
          <input inputMode="decimal" value={row.unitPrice} onChange={(e) => set({ unitPrice: decimalText(e.target.value) })} />
        </Field>
        {!fuel && (
          <Field label={`Consum (${row.unit}/ha)`}>
            <input inputMode="decimal" placeholder="ex: 0.5" value={row.consumptionPerHa} onChange={(e) => set({ consumptionPerHa: decimalText(e.target.value) })} />
          </Field>
        )}
      </div>
      <p className="st-row-total">Total produs: <strong>{number(rowTotal(row), 2)} lei</strong></p>
    </section>
  );
};

const InvoicePanel = ({ invoice, initialCompany, companies, suppliers, saving, onSave, onClose }) => {
  const [form, setForm] = useState(() => (invoice ? formFromInvoice(invoice) : emptyForm(initialCompany)));
  const [tried, setTried] = useState(false);
  const errors = tried ? formErrors(form) : {};
  const companyOptions = [...new Set([...companies, ...(form.company ? [form.company] : [])])];

  const changeRow = (index, patch) => setForm((prev) => ({ ...prev, rows: prev.rows.map((row, i) => (i === index ? { ...row, ...patch } : row)) }));
  const removeRow = (index) => setForm((prev) => ({ ...prev, rows: prev.rows.filter((_, i) => i !== index) }));

  const submit = (event) => {
    event.preventDefault();
    setTried(true);
    if (Object.keys(formErrors(form)).length === 0) onSave(form);
  };

  return (
    <Panel title={invoice ? 'Modifică factura' : 'Factură nouă'} onClose={onClose} onSubmit={submit}
      footer={(
        <>
          <button type="button" className="pm-btn-soft pm-only-wide" onClick={onClose}>Anulează</button>
          <button type="submit" className="pm-btn-accent" disabled={saving}>
            {saving ? 'Se salvează…' : `Salvează · ${number(formTotal(form), 2)} lei`}
          </button>
        </>
      )}>
      {Object.keys(errors).length > 0 && <p className="pm-error">Completează câmpurile marcate cu roșu.</p>}
      <div className="pm-two">
        <Field label="Număr factură" error={errors.number}>
          <input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} />
        </Field>
        <Field label="Data" error={errors.date}>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
      </div>
      <Field label="Firma cumpărătoare" error={errors.company}>
        <select value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}>
          <option value="">Alege</option>
          {companyOptions.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
      </Field>
      <Field label="Furnizor" error={errors.supplier}>
        <input list="st-suppliers" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
        <datalist id="st-suppliers">{suppliers.map((name) => <option key={name} value={name} />)}</datalist>
      </Field>
      <div className="pm-segments st-two" role="group" aria-label="Status">
        {['Plătită', 'Scadentă'].map((status) => (
          <button key={status} type="button" className={form.status === status ? 'is-on' : ''} aria-pressed={form.status === status} onClick={() => setForm({ ...form, status })}>{status}</button>
        ))}
      </div>

      {form.rows.map((row, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <InvoiceRowFields key={index} row={row} index={index} errors={errors} canRemove={form.rows.length > 1} onChange={changeRow} onRemove={removeRow} />
      ))}
      <button type="button" className="pm-btn-dashed" onClick={() => setForm({ ...form, rows: [...form.rows, emptyRow()] })}>+ Adaugă alt produs</button>
    </Panel>
  );
};

const InvoiceDetail = ({ invoice, allocations, canEdit, onEdit, onAllocate, onDelete, onClose }) => {
  const shares = (invoice.items || []).map((_, index) => allocations[`${invoice.id}_${index}`]?.companyAllocations).filter(Boolean);
  const byCompany = {};
  shares.forEach((share) => Object.entries(share).forEach(([company, part]) => { byCompany[company] = (byCompany[company] || 0) + (part.allocatedValue || 0); }));

  return (
    <Panel title={`Factura ${invoice.number || ''}`} subtitle={invoice.company} onClose={onClose}>
      {canEdit && (
        <div className="pm-actions">
          <button type="button" className="is-primary" onClick={onEdit}><Icon d={ICON.edit} />Modifică</button>
          <button type="button" disabled={!invoice.items?.length} onClick={onAllocate}><Icon d={ICON.chart} />Alocare automată</button>
          <button type="button" className="is-danger" onClick={onDelete}><Icon d={ICON.trash} />Șterge</button>
        </div>
      )}
      <div className="pm-card">
        <dl className="pm-rows">
          <div><dt>Furnizor</dt><dd>{invoice.supplier || '—'}</dd></div>
          <div><dt>Data</dt><dd>{formatDate(invoice.date)}</dd></div>
          <div><dt>Status</dt><dd className={invoice.status === 'Plătită' ? '' : 'pm-warn'}>{invoice.status === 'Plătită' ? 'Plătită' : 'Scadentă'}</dd></div>
          <div><dt>Total</dt><dd>{number(invoice.totalValue, 2)} lei</dd></div>
        </dl>
      </div>
      <div className="pm-card">
        <h3>Produse ({invoice.items?.length || 0})</h3>
        <dl className="pm-rows">
          {(invoice.items || []).map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index}>
              <dt>
                {item.productType}{item.productName ? `, ${item.productName}` : ''}
                <small className="st-small">{targetCropsOf(item).join(', ') || 'Toate culturile'}{item.consumptionPerHa ? ` · ${item.consumptionPerHa} ${item.unit}/ha` : ''}</small>
              </dt>
              <dd>
                {quantity(item.quantity)} {item.unit}
                <small className="st-small">{number(item.quantity * item.unitPrice, 2)} lei</small>
              </dd>
            </div>
          ))}
        </dl>
      </div>
      {Object.keys(byCompany).length > 0 && (
        <div className="pm-card">
          <h3>Alocare pe firme</h3>
          <dl className="pm-rows">
            {Object.entries(byCompany).filter(([, value]) => value > 0).map(([company, value]) => <div key={company}><dt>{company}</dt><dd>{lei(value)}</dd></div>)}
          </dl>
        </div>
      )}
    </Panel>
  );
};

// ---------- Setup ----------

const CropPanel = ({ company, crops, editName, onSave, onClose }) => {
  const editing = editName ? crops[editName] : null;
  const [name, setName] = useState(editName || '');
  const [hectares, setHectares] = useState(editing ? String(editing.hectares ?? '') : '');
  const [notes, setNotes] = useState(editing?.notes || '');
  const [tried, setTried] = useState(false);
  const validHa = parseFloat(hectares) >= 0;
  const replaces = !editName && crops[name];

  const submit = (event) => {
    event.preventDefault();
    setTried(true);
    if (name && validHa) onSave(company, { name, hectares, notes });
  };

  return (
    <Panel title={editName ? `Modifică ${editName}` : 'Adaugă cultură'} subtitle={company} onClose={onClose} onSubmit={submit}
      footer={(
        <>
          <button type="button" className="pm-btn-soft pm-only-wide" onClick={onClose}>Anulează</button>
          <button type="submit" className="pm-btn-primary">Salvează cultura</button>
        </>
      )}>
      {!editName && (
        <Field label="Cultura" error={tried && !name} hint={replaces ? `Există deja cu ${crops[name].hectares} ha. Noile hectare o vor înlocui.` : undefined}>
          <select value={name} onChange={(e) => setName(e.target.value)}>
            <option value="">Alege</option>
            {CROP_TYPES.map((crop) => <option key={crop} value={crop}>{crop}</option>)}
          </select>
        </Field>
      )}
      <Field label="Hectare" error={tried && !validHa}>
        <input inputMode="decimal" value={hectares} onChange={(e) => setHectares(decimalText(e.target.value))} />
      </Field>
      <Field label="Notițe">
        <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Panel>
  );
};

const TransferCard = ({ suggestion }) => (
  <section className="pm-card st-transfer">
    <div className="pm-card-head">
      <h3>{suggestion.productType}</h3>
      <span className="pm-muted">{suggestion.transfers.length} {suggestion.transfers.length === 1 ? 'transfer' : 'transferuri'}</span>
    </div>
    <p className="pm-muted">
      Necesar {quantity(suggestion.totalNeed)} {suggestion.unit}, disponibil {quantity(suggestion.totalSupply)} {suggestion.unit}.
      Consum mediu {quantity(suggestion.averageConsumptionPerHa)} {suggestion.unit}/ha.
    </p>
    {suggestion.transfers.map((transfer) => (
      <div key={`${transfer.from}-${transfer.to}`} className="st-move">
        <p className="st-move-who"><strong>{transfer.from}</strong> <span aria-hidden="true">→</span><span className="st-sr"> către </span> <strong>{transfer.to}</strong></p>
        <p className="st-move-big">{quantity(transfer.transferQuantity)} {suggestion.unit} · {lei(transfer.transferValue)}</p>
        <p className="pm-muted">{transfer.impact}</p>
        <details className="pm-more">
          <summary>Detalii</summary>
          <dl className="pm-rows">
            <div><dt>Preț</dt><dd>{number(transfer.pricePerUnit, 2)} lei/{suggestion.unit}</dd></div>
            <div><dt>Optimizare</dt><dd>{number(transfer.optimizationScore)}%</dd></div>
            <div><dt>{transfer.from}</dt><dd>are {quantity(transfer.fromCurrentSupply)}, necesar {quantity(transfer.fromNeed)}</dd></div>
            {transfer.fromCropDetails.map((crop) => <div key={`f${crop.cropName}`}><dt>{crop.cropName}, {formatHa(crop.hectares)} ha</dt><dd>necesar {quantity(crop.need)}</dd></div>)}
            <div><dt>{transfer.to}</dt><dd>are {quantity(transfer.toCurrentSupply)}, necesar {quantity(transfer.toNeed)}</dd></div>
            {transfer.toCropDetails.map((crop) => <div key={`t${crop.cropName}`}><dt>{crop.cropName}, {formatHa(crop.hectares)} ha</dt><dd>necesar {quantity(crop.need)}</dd></div>)}
          </dl>
        </details>
      </div>
    ))}
  </section>
);

// ---------- Page ----------

const StockAdjustment = ({ userRole, testStore }) => {
  const session = firebaseAuthService.getCurrentSession();
  const farmId = testStore ? null : session?.farm?.id;
  const canEdit = userRole !== 'worker';

  const [data, setData] = useState(() => (testStore
    ? { companies: testStore.companies, companyConfigs: testStore.companyConfigs, invoices: testStore.invoices, allocations: testStore.allocations }
    : {
      companies: (readSaved(`companySettings_${farmId}`, {}).companies || []).map(nameOf).filter(Boolean),
      companyConfigs: readSaved(`companyConfigs_${farmId}`, {}),
      invoices: readSaved(`invoices_${farmId}`, []),
      allocations: readSaved(`allocations_${farmId}`, {}),
    }));
  const [loading, setLoading] = useState(!testStore);
  const { companies, companyConfigs, invoices, allocations } = data;

  const [view, setView] = useState('stocks');
  const [company, setCompany] = useState('all');
  const [search, setSearch] = useState('');
  const [dueOnly, setDueOnly] = useState(false);
  const [shownCount, setShownCount] = useState(PAGE_SIZE);
  const [openLine, setOpenLine] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState(null); // { invoice } while the form is open
  const [cropForm, setCropForm] = useState(null); // { company, editName }
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = (message, type = 'success') => {
    clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), type === 'error' ? 6000 : 3500);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Live data: a change on any device shows up here straight away.
  useEffect(() => {
    if (testStore || !farmId) { setLoading(false); return undefined; }
    return onSnapshot(doc(db, 'farmData', farmId), (snapshot) => {
      const saved = snapshot.exists() ? snapshot.data() : {};
      const next = {
        companies: (saved.companySettings?.data?.companies || []).map(nameOf).filter(Boolean),
        companyConfigs: saved.companyConfigs?.data || {},
        invoices: Array.isArray(saved.invoices?.data) ? saved.invoices.data : [],
        allocations: saved.allocations?.data || {},
      };
      setData(next);
      setLoading(false);
      try {
        ['companyConfigs', 'invoices', 'allocations'].forEach((dataType) => {
          localStorage.setItem(`${dataType}_${farmId}`, JSON.stringify({ data: next[dataType], lastModified: new Date().toISOString(), synced: true, farmId, dataType }));
        });
      } catch { /* copy for offline use only */ }
    }, (error) => {
      console.error('Stoc: live data failed', error);
      setLoading(false);
    });
  }, [farmId, testStore]);

  // Reads the newest copy right before writing, so two devices cannot overwrite each other.
  const saveField = async (dataType, change) => {
    if (testStore) {
      const next = change(testStore[dataType]);
      testStore[dataType] = next;
      testStore.saves += 1;
      setData((prev) => ({ ...prev, [dataType]: next }));
      return;
    }
    const ref = doc(db, 'farmData', farmId);
    let next;
    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(ref);
      const saved = snapshot.data()?.[dataType]?.data;
      const isList = dataType === 'invoices';
      next = change(isList ? (Array.isArray(saved) ? saved : []) : (saved || {}));
      const now = new Date().toISOString();
      // mergeFields replaces this whole field, so a removed crop really goes away.
      transaction.set(ref, { [dataType]: { data: next, lastModified: now, dataType }, lastModified: now }, { mergeFields: [dataType, 'lastModified'] });
    });
    setData((prev) => ({ ...prev, [dataType]: next }));
  };

  const scopeCompanies = useMemo(() => (company === 'all' ? companies : companies.filter((name) => name === company)), [companies, company]);

  const breakdown = useMemo(() => companyCropBreakdown(companies, companyConfigs, invoices), [companies, companyConfigs, invoices]);
  const analysisResult = useMemo(() => {
    try {
      const analysis = analyzeProducts(companies, breakdown);
      return { analysis, suggestions: transferSuggestions(analysis), failed: false };
    } catch (error) {
      console.error('Stoc: transfer calculation failed', error);
      return { analysis: {}, suggestions: [], failed: true };
    }
  }, [companies, breakdown]);

  const linesByCompany = useMemo(() => scopeCompanies.map((name) => ({
    company: name, lines: stockLines(name, breakdown, analysisResult.analysis),
  })), [scopeCompanies, breakdown, analysisResult]);

  const scopeInvoices = useMemo(() => (company === 'all' ? invoices : invoices.filter((invoice) => invoice.company === company)), [invoices, company]);
  const belowNeed = linesByCompany.reduce((sum, group) => sum + group.lines.filter((line) => ['low', 'near'].includes(lineStatus(line))).length, 0);
  const dueInvoices = scopeInvoices.filter((invoice) => invoice.status !== 'Plătită');

  const shownInvoices = useMemo(() => scopeInvoices
    .filter((invoice) => matchesInvoice(invoice, search) && (!dueOnly || invoice.status !== 'Plătită'))
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))), [scopeInvoices, search, dueOnly]);

  const suppliers = useMemo(() => [...new Set([...readSuppliers(), ...invoices.map((invoice) => invoice.supplier).filter(Boolean)])]
    .sort((a, b) => a.localeCompare(b, 'ro')), [invoices, invoiceForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const detail = detailId ? invoices.find((invoice) => invoice.id === detailId) : null;

  const rememberSupplier = (name) => {
    const known = readSuppliers();
    if (!name || known.some((item) => item.toLowerCase() === name.toLowerCase())) return;
    try { localStorage.setItem('savedSuppliers', JSON.stringify([...known, name])); } catch { /* only a typing help */ }
  };

  // New invoices also reach the workers' inventory, as they always have.
  const syncToInventory = async (invoice) => {
    if (testStore) { testStore.inventorySyncs += 1; return; }
    try {
      const key = `agrimanage-inventory_${farmId}`;
      const current = JSON.parse(localStorage.getItem(key) || '{}');
      const updated = addInvoiceToInventory(invoice, current.data);
      localStorage.setItem(key, JSON.stringify({ data: updated, lastModified: new Date().toISOString(), synced: false, farmId, dataType: 'agrimanage-inventory' }));
      await universalDataService.saveUniversalData('agrimanage-inventory', updated);
      window.dispatchEvent(new CustomEvent('inventoryDataUpdated', { detail: { source: 'stockAdjustment' } }));
    } catch (error) {
      console.error('Stoc: inventory sync failed', error);
    }
  };

  const saveInvoice = async (form) => {
    if (saving) return;
    setSaving(true);
    const editing = invoiceForm.invoice;
    const invoice = buildInvoice(form, editing);
    try {
      await saveField('invoices', (list) => (editing
        ? list.map((item) => (item.id === editing.id ? invoice : item))
        : [...list, invoice]));
      rememberSupplier(invoice.supplier);
      if (!editing) await syncToInventory(invoice);
      setInvoiceForm(null);
      setDetailId(editing ? editing.id : null);
      showToast(editing ? 'Factura a fost modificată.' : 'Factura a fost salvată.');
    } catch (error) {
      console.error('Stoc: invoice save failed', error);
      showToast('Factura nu s-a salvat. Verifică internetul și încearcă din nou.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (invoice) => setConfirm({
    title: 'Ștergi factura?',
    text: `Factura ${invoice.number || ''} de la ${invoice.supplier || 'furnizor'}, ${number(invoice.totalValue, 2)} lei. O poți restaura din Coșul de gunoi, în Setări.`,
    action: 'Șterge factura',
    onYes: async () => {
      setConfirm(null);
      try {
        if (testStore) {
          testStore.deleted.push(invoice);
        } else {
          const current = await firebaseAuthService.getCurrentUser().catch(() => null);
          deletedItemsService.saveToRecycleBin(farmId, invoice, 'invoice',
            `Factură ${invoice.number || invoice.id} — ${invoice.company || ''} — ${invoice.totalValue?.toFixed(2) || '0'} LEI`,
            current?.user?.email || current?.user?.name || 'utilizator');
        }
        await saveField('invoices', (list) => list.filter((item) => item.id !== invoice.id));
        setDetailId(null);
        showToast('Factura a fost ștearsă. O poți restaura din Coșul de gunoi.');
      } catch (error) {
        console.error('Stoc: delete failed', error);
        showToast('Factura nu s-a șters. Verifică internetul și încearcă din nou.', 'error');
      }
    },
  });

  const askAllocate = (invoice) => setConfirm({
    title: 'Alocare automată?',
    text: 'Valoarea facturii se împarte pe toate firmele și culturile lor, după hectare. O alocare făcută înainte pentru această factură se înlocuiește.',
    action: 'Alocă',
    onYes: async () => {
      setConfirm(null);
      const next = invoiceAllocations(invoice, companies, companyConfigs);
      if (Object.values(next).every((allocation) => !allocation.companyAllocations)) {
        showToast('Nu sunt hectare în Configurare, deci nu am cum să împart factura.', 'error');
        return;
      }
      try {
        await saveField('allocations', (current) => ({ ...current, ...next }));
        showToast('Factura a fost alocată pe firme.');
      } catch (error) {
        console.error('Stoc: allocation failed', error);
        showToast('Alocarea nu s-a salvat. Verifică internetul.', 'error');
      }
    },
  });

  const saveCrop = async (name, crop) => {
    try {
      await saveField('companyConfigs', (current) => {
        const config = current[name] || { crops: {} };
        return { ...current, [name]: { ...config, crops: { ...config.crops, [crop.name]: { hectares: parseFloat(crop.hectares) || 0, notes: crop.notes || '' } } } };
      });
      setCropForm(null);
      showToast(`${crop.name} a fost salvată la ${name}.`);
    } catch (error) {
      console.error('Stoc: crop save failed', error);
      showToast('Cultura nu s-a salvat. Verifică internetul.', 'error');
    }
  };

  const askRemoveCrop = (name, cropName) => setConfirm({
    title: `Ștergi ${cropName}?`,
    text: `${cropName} dispare din ${name}. Necesarul și sugestiile de transfer se recalculează fără ea.`,
    action: 'Șterge cultura',
    onYes: async () => {
      setConfirm(null);
      try {
        await saveField('companyConfigs', (current) => {
          const config = current[name] || { crops: {} };
          const crops = { ...config.crops };
          delete crops[cropName];
          return { ...current, [name]: { ...config, crops } };
        });
        showToast(`${cropName} a fost ștearsă.`);
      } catch (error) {
        console.error('Stoc: crop remove failed', error);
        showToast('Cultura nu s-a șters. Verifică internetul.', 'error');
      }
    },
  });

  const downloadReport = () => {
    const report = complianceReport({ farm: testStore ? { name: 'Test' } : session?.farm, companies, companyConfigs, invoices, allocations });
    const url = URL.createObjectURL(new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `raport-conformitate-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast('Raportul a fost descărcat.');
  };

  const openNewInvoice = () => setInvoiceForm({ invoice: null });
  const issues = setupIssues(companies, companyConfigs);
  const cropTotal = companies.reduce((sum, name) => sum + Object.keys(companyConfigs[name]?.crops || {}).length, 0);
  const anyLines = linesByCompany.some((group) => group.lines.length > 0);

  return (
    <div className="pm st">
      <header className="pm-head">
        <h1>Stoc</h1>
        <div className="pm-head-actions">
          {canEdit && <button type="button" className="pm-btn-accent pm-only-wide" onClick={openNewInvoice}><Icon d={ICON.plus} size={18} />Factură</button>}
          {companies.length > 1 && (
            <select className="pm-chip st-company" aria-label="Firma" value={company} onChange={(e) => { setCompany(e.target.value); setShownCount(PAGE_SIZE); }}>
              <option value="all">Toate firmele</option>
              {companies.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          )}
        </div>
      </header>

      <div className="pm-chips st-views" role="group" aria-label="Secțiuni">
        {VIEWS.map((item) => (
          <button key={item.id} type="button" className={`pm-chip ${view === item.id ? 'is-on' : ''}`} aria-pressed={view === item.id} onClick={() => setView(item.id)}>
            {item.label}{item.id === 'invoices' ? ` ${scopeInvoices.length}` : ''}
          </button>
        ))}
      </div>

      {loading && <p className="pm-empty">Se încarcă datele…</p>}

      {!loading && view === 'stocks' && (
        <>
          <div className="st-tiles">
            <div><strong>{belowNeed}</strong><span className="is-low">produse sub necesar</span></div>
            <button type="button" onClick={() => { setDueOnly(true); setSearch(''); setView('invoices'); }}>
              <strong>{dueInvoices.length}</strong><span className="is-near">facturi scadente</span>
            </button>
          </div>
          {!anyLines && <p className="pm-empty">{invoices.length === 0 ? 'Nu ai încă produse în stoc. Adaugă prima factură.' : 'Aceste firme nu au produse în stoc.'}</p>}
          {linesByCompany.filter((group) => group.lines.length > 0).map((group) => (
            <section key={group.company} className="st-group">
              {company === 'all' && <h2>{group.company}</h2>}
              <div className="pm-list">
                {group.lines.map((line) => <StockRow key={line.type} line={line} onOpen={() => setOpenLine(line)} />)}
              </div>
            </section>
          ))}
        </>
      )}

      {!loading && view === 'invoices' && (
        <>
          <label className="pm-search">
            <Icon d={ICON.search} />
            <input type="search" placeholder="Caută număr, furnizor, produs" aria-label="Caută" value={search}
              onChange={(e) => { setSearch(e.target.value); setShownCount(PAGE_SIZE); }} />
          </label>
          <div className="pm-chips">
            <button type="button" className={`pm-chip ${dueOnly ? 'is-on' : ''}`} aria-pressed={dueOnly} onClick={() => { setDueOnly(!dueOnly); setShownCount(PAGE_SIZE); }}>
              Doar scadente {dueInvoices.length}
            </button>
          </div>
          <div className="pm-list">
            {shownInvoices.length === 0 && <p className="pm-empty">{invoices.length === 0 ? 'Nu există facturi. Adaugă prima factură.' : 'Nu am găsit nicio factură.'}</p>}
            {shownInvoices.slice(0, shownCount).map((invoice) => (
              <button key={invoice.id} type="button" className="pm-row" onClick={() => setDetailId(invoice.id)}>
                <div className="pm-row-main">
                  <strong>{invoice.number || 'Fără număr'} · {invoice.supplier || 'Fără furnizor'}</strong>
                  <span>{formatDate(invoice.date)}{company === 'all' ? ` · ${invoice.company}` : ''} · {invoice.items?.length || 0} {invoice.items?.length === 1 ? 'produs' : 'produse'}</span>
                  {invoice.status !== 'Plătită' && <span className="pm-warn">Scadentă</span>}
                </div>
                <span className="pm-row-end">{lei(invoice.totalValue)}</span>
              </button>
            ))}
            {shownInvoices.length > shownCount && (
              <button type="button" className="pm-btn-dashed" onClick={() => setShownCount(shownCount + PAGE_SIZE)}>
                Arată mai multe ({shownInvoices.length - shownCount})
              </button>
            )}
          </div>
        </>
      )}

      {!loading && view === 'summary' && (
        <>
          {scopeCompanies.map((name) => {
            const stats = breakdown[name];
            return (
              <section key={name} className="pm-card st-summary">
                <div className="pm-card-head"><h3>{name}</h3></div>
                <dl className="pm-rows">
                  <div><dt>Hectare</dt><dd>{formatHa(stats.totalHectares)} ha</dd></div>
                  <div><dt>Investiție în facturi</dt><dd>{lei(stats.totalInvestment)}</dd></div>
                  {Object.entries(stats.crops).map(([crop, cropData]) => <div key={crop}><dt>{crop}</dt><dd>{formatHa(cropData.hectares)} ha</dd></div>)}
                </dl>
              </section>
            );
          })}
          <h2 className="st-heading">Sugestii de transfer</h2>
          {analysisResult.failed && <p className="pm-error">Sugestiile nu s-au putut calcula. Verifică facturile și culturile din Configurare.</p>}
          {!analysisResult.failed && analysisResult.suggestions.length === 0 && (
            <p className="pm-note">Nu sunt transferuri de sugerat. Fie stocurile sunt echilibrate, fie lipsesc facturile sau consumul pe hectar.</p>
          )}
          {analysisResult.suggestions.map((suggestion) => <TransferCard key={suggestion.productType} suggestion={suggestion} />)}
          {analysisResult.suggestions.length > 0 && (
            <p className="pm-note">Transferul se face printr-o factură de la firma cu surplus către firma cu deficit. Necesarul este hectare × consum pe hectar.</p>
          )}
        </>
      )}

      {!loading && view === 'reports' && (
        <>
          <div className="ar-tiles st-report-tiles">
            <div><strong>{companies.length}</strong><span>Firme</span></div>
            <div><strong>{invoices.length}</strong><span>Facturi</span></div>
            <div><strong>{cropTotal}</strong><span>Culturi</span></div>
            <div><strong>{Object.keys(allocations).length}</strong><span>Alocări</span></div>
          </div>
          <section className="pm-card st-report">
            <h3>Raport de conformitate</h3>
            <p className="pm-muted">Conține firmele cu culturile lor, toate facturile, alocările și costurile pe hectar. Se descarcă ca fișier JSON.</p>
            <button type="button" className="pm-btn-primary" onClick={downloadReport}><Icon d={ICON.doc} size={18} />Descarcă raportul</button>
          </section>
        </>
      )}

      {!loading && view === 'setup' && (
        <>
          {issues.length > 0 && <div className="pm-note st-issues">{issues.map((issue) => <p key={issue}>{issue}</p>)}</div>}
          {companies.length > 0 && <p className="pm-muted st-hint">Firmele se adaugă din Setări. Aici pui culturile și hectarele fiecăreia.</p>}
          {scopeCompanies.map((name) => {
            const crops = companyConfigs[name]?.crops || {};
            const stats = companyStats(name, companyConfigs, allocations, invoices);
            return (
              <section key={name} className="pm-card st-setup">
                <div className="pm-card-head">
                  <h3>{name}</h3>
                  <span className="pm-muted">{formatHa(stats.totalHectares)} ha · {stats.cropCount} {stats.cropCount === 1 ? 'cultură' : 'culturi'}</span>
                </div>
                {stats.totalAllocatedCost > 0 && <p className="pm-muted">Cost alocat {lei(stats.totalAllocatedCost)}, adică {number(stats.costPerHectare, 2)} lei/ha.</p>}
                <div className="st-crop-list">
                  {Object.entries(crops).map(([crop, cropData]) => (
                    <div key={crop}>
                      <span><strong>{crop}</strong>{cropData.notes && <small className="st-small">{cropData.notes}</small>}</span>
                      <span className="st-crop-ha">{formatHa(cropData.hectares)} ha</span>
                      {canEdit && (
                        <>
                          <button type="button" className="pm-icon-btn" aria-label={`Modifică ${crop} la ${name}`} onClick={() => setCropForm({ company: name, editName: crop })}><Icon d={ICON.edit} size={18} /></button>
                          <button type="button" className="pm-icon-btn" aria-label={`Șterge ${crop} la ${name}`} onClick={() => askRemoveCrop(name, crop)}><Icon d={ICON.trash} size={18} /></button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                {canEdit && <button type="button" className="pm-btn-dashed" onClick={() => setCropForm({ company: name, editName: null })}>+ Adaugă cultură</button>}
              </section>
            );
          })}
        </>
      )}

      {canEdit && !loading && (view === 'stocks' || view === 'invoices') && (
        <button type="button" className="pm-fab st-fab" onClick={openNewInvoice}><Icon d={ICON.plus} size={22} />Factură</button>
      )}

      {openLine && <ProductPanel line={openLine} onClose={() => setOpenLine(null)} />}

      {detail && !invoiceForm && (
        <InvoiceDetail invoice={detail} allocations={allocations} canEdit={canEdit} onClose={() => setDetailId(null)}
          onEdit={() => setInvoiceForm({ invoice: detail })} onAllocate={() => askAllocate(detail)} onDelete={() => askDelete(detail)} />
      )}

      {invoiceForm && (
        <InvoicePanel invoice={invoiceForm.invoice} initialCompany={company !== 'all' ? company : ''} companies={companies} suppliers={suppliers}
          saving={saving} onSave={saveInvoice} onClose={() => setInvoiceForm(null)} />
      )}

      {cropForm && (
        <CropPanel company={cropForm.company} editName={cropForm.editName} crops={companyConfigs[cropForm.company]?.crops || {}}
          onSave={saveCrop} onClose={() => setCropForm(null)} />
      )}

      {confirm && <ConfirmPanel {...confirm} onClose={() => setConfirm(null)} />}

      {toast && <div className={`pm-toast is-${toast.type}`} role="status">{toast.message}</div>}
    </div>
  );
};

export default StockAdjustment;
